import { useEffect, useRef, useState } from 'react';
import { useContainer } from '../../useContainer';
import { AbsenceReportApi } from '../../../api';
import { AbsenceReportApiResponse } from '../../../api/AbsenceReportApi';
import useHttpTriggerAbsenceCompute from './useHttpTriggerAbsenceCompute';

type AbsenceOverviewData = AbsenceReportApiResponse.GetOverview['body'];

const POLL_INTERVAL_MS = 4000;
const COMPUTE_PENDING_KEY = 'absence-overview-compute-pending';

const useHttpGetAbsenceOverview = () => {
    const absenceReportApi: AbsenceReportApi = useContainer(AbsenceReportApi);
    const { trigger: triggerCompute } = useHttpTriggerAbsenceCompute();
    const [data, setData] = useState<AbsenceOverviewData>({ dataAvailable: false });
    const [isReady, setReady] = useState(false);
    const [isError, setError] = useState(false);
    const abortRef = useRef<(() => void) | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        let unmounted = false;

        const stopPolling = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        const fetchOverview = (onResult: (body: AbsenceOverviewData) => void) => {
            const { exec, abort } = absenceReportApi.getOverview({
                onSuccess: res => {
                    setData(res.body);
                    setError(false);
                    onResult(res.body);
                },
                onError: () => setError(true),
                onFinally: () => setReady(true),
            });
            abortRef.current = abort;
            exec();
        };

        const startPolling = () => {
            if (unmounted) return;
            stopPolling();
            intervalRef.current = setInterval(() => {
                fetchOverview(body => {
                    if (body.dataAvailable) {
                        stopPolling();
                        sessionStorage.removeItem(COMPUTE_PENDING_KEY);
                    }
                });
            }, POLL_INTERVAL_MS);
        };

        fetchOverview(body => {
            if (body.dataAvailable) {
                sessionStorage.removeItem(COMPUTE_PENDING_KEY);
                return;
            }

            if (sessionStorage.getItem(COMPUTE_PENDING_KEY) === 'true') {
                startPolling();
                return;
            }

            sessionStorage.setItem(COMPUTE_PENDING_KEY, 'true');
            triggerCompute(
                () => {
                    if (!unmounted) startPolling();
                },
                () => {
                    sessionStorage.removeItem(COMPUTE_PENDING_KEY);
                    if (!unmounted) setError(true);
                }
            );
        });

        return () => {
            unmounted = true;
            stopPolling();
            abortRef.current?.();
        };
    }, [absenceReportApi]);

    return { data, isReady, isError };
};

export default useHttpGetAbsenceOverview;
